/**
 * THE TAIL OF THE ARRIVAL CEREMONY.
 *
 * A new player is owed a SEQUENCE, one screen at a time: the starter Stardust
 * grant, then a referral payout if one is waiting, then the invitation brief —
 * which does not evaporate when dismissed but flies into the quest rail. Each
 * of those already hands off to the next as it CLOSES (see starter-reward.js);
 * firing them together put two celebration overlays on one frame.
 *
 * Founder's Legacy joins the end of that sequence. It used to be granted at
 * signup and acknowledged in the same write, so the first cardback a player
 * owned was the only one that never announced itself. Draining it at lobby
 * entry instead would put a cardback banner on top of the 500-Stardust screen,
 * which is the exact collision the chain exists to prevent.
 *
 * So: whoever wants to run after the ceremony QUEUES, each screen HOLDS while
 * it is on screen and RELEASES when it closes, and the queued work runs when
 * the chain has been kicked off and nothing is holding.
 *
 * The short settle window exists because the handoff is asynchronous — the
 * invitation brief asks the server whether this account was really invited
 * before it can show, so there is a real gap between one screen releasing and
 * the next taking its hold. Without the window the tail fires into that gap.
 * It is a sequencing delay, never a fallback: if a branch never releases, the
 * banner simply waits for the next launch, exactly as it did before.
 */

const SETTLE_WINDOW_MS = 400;

const queued = [];
let holds = 0;
let started = false;
let settleTimer = null;

function clearSettleTimer() {
  if (settleTimer === null) return;
  clearTimeout(settleTimer);
  settleTimer = null;
}

function runIfSettled() {
  if (!started || holds > 0 || queued.length === 0) return;
  // Drained as a list, never as a single slot: a second caller must not be
  // able to silently evict the first one's work.
  const pending = queued.splice(0, queued.length);
  for (const run of pending) void run();
}

function scheduleSettleCheck() {
  clearSettleTimer();
  settleTimer = setTimeout(() => {
    settleTimer = null;
    runIfSettled();
  }, SETTLE_WINDOW_MS);
}

export const ArrivalCeremony = {
  /** Run this once the arrival screens are done. Additive; order preserved. */
  queue(run) {
    if (typeof run === 'function') queued.push(run);
  },

  /** The chain has been kicked off; nothing shown means nothing to wait for. */
  begin() {
    started = true;
    if (holds > 0) return;
    scheduleSettleCheck();
  },

  /** An arrival screen is on the player's frame. */
  hold() {
    holds += 1;
    clearSettleTimer();
  },

  /** That screen has finished closing. */
  release() {
    holds = Math.max(0, holds - 1);
    if (holds > 0) return;
    scheduleSettleCheck();
  },

  /** Sign-out and tests: forget everything without running it. */
  reset() {
    clearSettleTimer();
    queued.length = 0;
    holds = 0;
    started = false;
  },
};
